import React from "react";
import styled from "styled-components";
import Window from "../Window";
import DesktopIcon from "../DesktopIcon";
import PixelIcon from "../PixelIcon";

// The Recycle Bin: sits in the bottom-left corner of the desktop, empty
// by default. Desktop icons dragged onto it land inside as restorable
// items.

export function RecycleBinIcon({ onOpen, full = false }) {
  return (
    <DesktopIcon
      icon={
        <PixelIcon
          name={full ? "recycle-bin-full" : "recycle-bin-empty"}
          size={32}
        />
      }
      label="Recycle Bin"
      onOpen={onOpen}
    />
  );
}

// items: [{ label, iconName, onRestore }] — things thrown in this
// session. Double-click restores (simplest gesture that reads; the
// real bin used File → Restore).
export function RecycleBinWindow({ onClose, inactive = false, items = [] }) {
  const [selected, setSelected] = React.useState(null);
  const iconName = items.length ? "recycle-bin-full" : "recycle-bin-empty";
  // Double-click counted from click events so touch double-taps work
  // too (see DesktopIcon — dblclick never fires for taps).
  const lastTapRef = React.useRef({ label: null, t: 0 });
  const tapItem = (item) => {
    const last = lastTapRef.current;
    const now = Date.now();
    lastTapRef.current = { label: item.label, t: now };
    if (last.label === item.label && now - last.t < 500) {
      setSelected(null);
      if (item.onRestore) item.onRestore();
    } else {
      setSelected(item.label);
    }
  };
  return (
    <StyledWindow
      title="Recycle Bin"
      icon={<PixelIcon name={iconName} size={16} />}
      inactive={inactive}
      controls={["close"]}
      onControlClick={(control) => {
        if (control === "close" && onClose) onClose();
      }}
      statusFields={[`${items.length} object(s)`]}
    >
      <Pane onMouseDown={() => setSelected(null)}>
        {items.map((item) => (
          <FileItem
            key={item.label}
            type="button"
            data-selected={selected === item.label ? "true" : undefined}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => tapItem(item)}
            title="Double-click to restore"
          >
            <PixelIcon name={item.iconName || "text-file"} size={16} />
            <em>{item.label}</em>
          </FileItem>
        ))}
      </Pane>
    </StyledWindow>
  );
}

const StyledWindow = styled(Window)`
  width: 380px;
  height: 280px;
`;

const Pane = styled.div`
  height: 100%;
  background: #fff;
  border: 2px solid;
  border-color: #808080 #fff #fff #808080;
  padding: 8px;
`;

const FileItem = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  box-shadow: none;
  min-width: 0;
  min-height: 0;
  padding: 2px 6px;
  font: inherit;
  font-size: 13px;
  color: inherit;

  & em {
    font-style: normal;
  }
  &&[data-selected="true"] em {
    background: #00007b;
    color: #fff;
  }
  &&:not(:disabled):hover,
  &&:not(:disabled):active {
    box-shadow: none;
  }
  &&:focus {
    outline: none;
  }
`;

export default RecycleBinWindow;
